import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gift, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ReferralLanding() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/referrals/by-code/${code}`)
      .then(({ data }) => { if (!cancelled) setReferral(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Referral not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
  }, [code]);

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post("/referrals/redeem", { code });
      toast.success("Offer claimed — check your inbox!");
      const fresh = await api.get(`/referrals/by-code/${code}`);
      setReferral(fresh.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto px-6 py-16 text-gray-500">Loading…</div>;
  if (error || !referral) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card className="rounded-[24px] p-8 text-center border border-border">
          <h2 className="font-display text-2xl tracking-tighter">Referral not found</h2>
          <p className="mt-3 text-gray-600">{error || "This referral code is no longer valid."}</p>
          <Link to="/"><Button variant="outline" className="mt-6 rounded-[35px]">Back to Servrevu</Button></Link>
        </Card>
      </div>
    );
  }

  const offer = referral.referee_offer || {};
  const alreadyClaimed = referral.status === "claimed" || referral.status === "redeemed";

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <Card className="rounded-[35px] overflow-hidden border border-border" data-testid="referral-landing">
        <div className="bg-gradient-to-br from-[#B31E1E] to-[#6B1010] text-white p-8">
          <div className="overline text-white/70">Referral from {referral.referrer_name || "a friend"}</div>
          <h1 className="font-display text-4xl tracking-tighter mt-2">{offer.title || "Welcome offer"}</h1>
          <p className="mt-2 text-white/80">at <Link to={`/businesses/${referral.business_id}`} className="underline hover:text-white">{referral.business_name}</Link></p>
        </div>
        <div className="p-8 space-y-5">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{offer.description}</p>
          {offer.discount_code && (
            <div className="text-center">
              <div className="overline text-gray-500 mb-2">Your code</div>
              <div className="inline-block border-2 border-dashed border-[#B31E1E] bg-[#eef2ff] text-[#B31E1E] font-mono font-bold tracking-[0.2em] px-5 py-3 rounded-md text-lg" data-testid="referral-code-display">
                {offer.discount_code}
              </div>
            </div>
          )}
          {offer.expires_at && (
            <p className="text-xs text-gray-500 text-center">Offer valid through {offer.expires_at}</p>
          )}

          {alreadyClaimed ? (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-2" data-testid="referral-claimed-msg">
              <Check size={18} className="text-emerald-700" />
              <span className="text-sm text-emerald-900">You've already claimed this offer — keep the code above handy when you visit.</span>
            </div>
          ) : !user ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="referral-signin-prompt">
              Sign in or create a free customer account on Servrevu to claim this offer — it takes 30 seconds, and your friend will get their thank-you reward automatically.
              <div className="flex gap-2 mt-3">
                <Link to={`/login?next=/r/${code}`}><Button size="sm" className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]">Sign in</Button></Link>
                <Link to={`/register?next=/r/${code}`}><Button size="sm" variant="outline" className="rounded-[35px]">Create free account</Button></Link>
              </div>
            </div>
          ) : user.role !== "customer" ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
              Only customer accounts can claim referral offers. Sign in with a customer account to claim.
            </div>
          ) : (
            <Button onClick={claim} disabled={claiming} className="w-full rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] h-12 text-base" data-testid="claim-referral-button">
              <Gift size={18} className="mr-2" />{claiming ? "Claiming…" : "Claim my welcome offer"}
            </Button>
          )}

          <Link to={`/businesses/${referral.business_id}`} className="block text-center text-sm text-[#B31E1E] hover:underline">
            Visit {referral.business_name} on Servrevu <ArrowRight size={12} className="inline" />
          </Link>
        </div>
      </Card>
    </div>
  );
}
